import React, {useEffect} from 'react';
import I18nProvider from '@nl/i18n';
import i18nLocales from '@nl/i18n-locales';
import ImageProxy from '@nl/image-proxy';
import {MainRoute} from '@nl/core';
import {now} from '@nl/neu';
import {hot} from 'react-hot-loader/root';
import {Error, getConfig, useActions, useDeepEffect, useSearch} from '@nl/ui';
import {shallowEqual, useSelector} from 'react-redux';
import useScrollToTop from './hooks/useScrollToTop';
import useTrackers from './hooks/useTrackers';
import useResetPersonal from './hooks/useResetPersonal';
import useFavorite from './hooks/useFavorite';
import useAdInitialization from './hooks/useAdInitialization';
import useEvent from './hooks/useEvent';
import routes from './router';
import Header from './pages/Header';
import FreeSampleCountdown from './components/freesample/FreeSampleCountdown';
import projectLocales from './assets/i18n';
import {PageLoading, ContentLoading} from './components/loading';
import AppModal from './components/modal/AppModal';
import {init} from './utils/auth';
import {clearCache} from './utils/clearCache';
import {setUserStatus, setInitialized, setLocaleLanguage, setHideScore} from './store/actions/client';
import useHideNavParam from './hooks/useHideNavParam';

function App() {
	const [getConfigAction] = useActions([getConfig]);
	const [
		setClientAction,
		setInitializedAction,
		setLocaleLanguageAction,
		setHideScoreAction,
	] = useActions([setUserStatus, setInitialized, setLocaleLanguage, setHideScore]);
	const {
		config: {
			I18N_URL,
			cdn,
			imageConfig,
			isDomestic,
		}, initialized, locale, timeOffset,
	} = useSelector(
			(state) => ({
				config: state.config.data,
				initialized: state.client.initialized,
				locale: state.client.data.locale,
				timeOffset: state.client.data.timeOffset,
			}),
			shallowEqual,
	);
	const {data} = useSelector((state) => state.config, shallowEqual);
	const {isRequested, failed} = useSelector((state) => state.config, shallowEqual);
	const flushCache = useSearch('flushcache');

	useAdInitialization();
	useTrackers();
	useResetPersonal();
	useFavorite();
	useEvent();
	useScrollToTop();
	useHideNavParam();

	useEffect(() => {
		if (flushCache !== null) {
			clearCache();
		}
		getConfigAction();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!isNaN(timeOffset)) {
			const time = now().getTime() - timeOffset;
			now(time);
		}
	}, [timeOffset]);

	useDeepEffect(() => {
		if (cdn && imageConfig) {
			ImageProxy.init(cdn.content, imageConfig);
		}
	}, [cdn, imageConfig]);

	if (!failed && !isRequested) {
		return (<PageLoading />);
	}
	init(isDomestic, false, setClientAction, setInitializedAction, setLocaleLanguageAction, data,
			setHideScoreAction);
	if (!failed && !initialized) {
		return (<PageLoading />);
	}
	return (
			<I18nProvider locales={[i18nLocales, projectLocales]} locale={locale} externalUrl={I18N_URL}>
				<Header />
				<FreeSampleCountdown />
				{failed ? <Error /> : <MainRoute Loading={ContentLoading} routes={routes} />}
				<AppModal />
			</I18nProvider>
	);
}

export default hot(App);
